<template>
    <el-container style="height: calc(100vh - 52px)">
        <el-header style="height: auto;" class="bg-gray-200 p-3 md:p-5">
            <div class="flex w-full flex-col md:flex-row">
                <!-- 検索部 -->
                <div class="hidden md:block bg-white border-2 border-gray-400 rounded-md p-4">
                    <el-form :model="searchForm" label-width="auto" label-position="top">
                        <div class="grid gap-4"
                            :class="[
                                loginUserRole === ROLES.SYSTEM ? 'md:grid-cols-5' : (loginUserRole === ROLES.ADMIN && loginUserRoleIsHead ? 'md:grid-cols-4' : 'md:grid-cols-3'),
                            ]">
                            <el-form-item class="no-margin"
                                v-show="loginUserRole === ROLES.SYSTEM" label="組織">
                                <OrganizationSelect v-model="searchForm.organization_id" :is-search="true" placeholder="組織を選択" />
                            </el-form-item>

                            <el-form-item class="no-margin" v-show="loginUserRole === ROLES.SYSTEM || (loginUserRole === ROLES.ADMIN && loginUserRoleIsHead)" label="部門">
                                <DepartmentSelect :organization-id="searchForm.organization_id" :is-search="true" 
                                    v-model="searchForm.department_id" placeholder="部門を選択" />
                            </el-form-item>

                            <el-form-item class="no-margin" label="カテゴリ">
                                <CategorySelect :organization-id="searchForm.organization_id"
                                    v-model="searchForm.category_id" :is-search="true" placeholder="カテゴリを選択" />
                            </el-form-item>

                            <el-form-item class="no-margin" label="アイテム名">
                                <AutoCompleteInput 
                                    v-model="searchForm.material_name" 
                                    placeholder="アイテム名を入力"
                                    storage-key="material-name-history"
                                    :max-history-items="15"
                                    ref="materialNameInput"
                                />
                            </el-form-item>

                            <div class="flex justify-end mt-7">
                                <el-button class="hidden md:block w-24" type="info" @click="handleSearchButton">検索</el-button>
                            </div>
                        </div>
                    </el-form>
                </div>

                <el-drawer class="md:hidden bg-gray-200" v-model="drawer" title="検索" direction="ttb" :with-header="false" size="auto">
                    <div class="bg-white border-2 border-gray-400 rounded-md p-4">
                        <el-form :model="searchForm" label-width="auto" label-position="top">
                            <div class="grid gap-4">
                                <el-form-item class="no-margin"
                                    v-show="loginUserRole === ROLES.SYSTEM" label="組織">
                                    <OrganizationSelect v-model="searchForm.organization_id" :is-search="true" placeholder="組織を選択" />
                                </el-form-item>

                                <el-form-item class="no-margin" v-show="loginUserRole === ROLES.SYSTEM || (loginUserRole === ROLES.ADMIN && loginUserRoleIsHead)" label="部門">
                                    <DepartmentSelect :organization-id="searchForm.organization_id" :is-search="true" 
                                        v-model="searchForm.department_id" placeholder="部門を選択" />
                                </el-form-item>

                                <el-form-item class="no-margin" label="カテゴリ">
                                    <CategorySelect :organization-id="searchForm.organization_id"
                                        v-model="searchForm.category_id" :is-search="true" placeholder="カテゴリを選択" />
                                </el-form-item>

                                <div class="flex">
                                    <el-form-item class="no-margin flex-grow" label="アイテム名">
                                        <AutoCompleteInput
                                            v-model="searchForm.material_name" 
                                            placeholder="アイテム名を入力"
                                            storage-key="material-name-history"
                                            :max-history-items="15"
                                            ref="materialNameInput"
                                        />
                                    </el-form-item>

                                    <div class="flex justify-end mt-7">
                                        <el-button class="md:hidden ml-3" type="info" @click="handleSearchButton" circle>
                                            <el-icon>
                                                <span class="material-symbols-outlined">manage_search</span>
                                            </el-icon>
                                        </el-button>
                                    </div>
                                </div>
                            </div>
                        </el-form>
                    </div>
                </el-drawer>

                <!-- 操作部 -->
                <div class="flex justify-end items-center md:flex-grow">
                    <div class="flex flex-grow items-center xl:justify-end">
                        <div class="md:hidden mr-1">
                            <el-button v-if="!isGrid" @click="updateGridMode(true)" circle>
                                <span class="material-symbols-outlined">
                                    lists
                                </span>
                            </el-button>

                            <el-button v-else @click="updateGridMode(false)" circle>
                                <span class="material-symbols-outlined">
                                    grid_view
                                </span>
                            </el-button>
                        </div>

                        <div class="md:hidden xl:block">
                            <div class="flex justify-end">
                                <span class="text-xs md:text-sm m-2 font-bold">アラート区分</span>
                                <div class="flex flex-wrap gap-x-2">
                                    <div class="flex items-center">
                                        <div class="h-3 w-3 bg-[#E63415] rounded-sm"></div>
                                        <span class="ml-1 text-xs md:text-sm">1日以内</span>
                                    </div>
                                    <div class="flex items-center">
                                        <div class="h-3 w-3 bg-[#FF6600] rounded-sm"></div>
                                        <span class="ml-1 text-xs md:text-sm">1日前</span>
                                    </div>
                                    <div class="flex items-center">
                                        <div class="h-3 w-3 bg-[#FFDE0A] rounded-sm"></div>
                                        <span class="ml-1 text-xs md:text-sm">7日前</span>
                                    </div>
                                    <div class="flex items-center">
                                        <div class="h-3 w-3 bg-[#1EC79D] rounded-sm"></div>
                                        <span class="ml-1 text-xs md:text-sm">15日前</span>
                                    </div>
                                    <div class="flex items-center">
                                        <div class="h-3 w-3 bg-[#14CCCC] rounded-sm"></div>
                                        <span class="ml-1 text-xs md:text-sm">30日前</span>
                                    </div>
                                    <div class="flex items-center">
                                        <div class="h-3 w-3 bg-[#4167F0] rounded-sm"></div>
                                        <span class="ml-1 text-xs md:text-sm">在庫数</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div class="flex flex-col">
                        <div class="flex justify-end">
                            <el-button class="custom-icon-btn md:hidden" type="info" 
                                @click="drawer = true" circle>
                                <span class="material-symbols-outlined">
                                    manage_search
                                </span>
                            </el-button>

                            <el-button class="custom-icon-btn md:hidden" type="info"
                                @click="handleAddButton" circle>
                                <span class="material-symbols-outlined">
                                    add
                                </span>
                            </el-button>
                            <el-button class="hidden md:block w-24" type="info"
                                @click="handleAddButton">新規登録</el-button>

                            <el-upload class="" ref="upload" :on-change="handleImportButton" :auto-upload="false"
                                :show-file-list="false" accept=".csv">
                                <el-tooltip content="CSVインポート" placement="top">
                                    <el-button class="custom-icon-btn mx-3" type="info" circle>
                                        <span class="material-symbols-outlined">
                                            upload_2
                                    </span>
                                </el-button>
                                </el-tooltip>
                            </el-upload>

                            <el-button class="custom-icon-btn" type="info" 
                                @click="handleExportButton" circle>
                                <el-tooltip content="CSVエクスポート" placement="top">
                                    <span class="material-symbols-outlined">
                                        download_2
                                    </span>
                                </el-tooltip>
                            </el-button>

                            <div class="hidden md:block ml-3">
                                <el-button
                                    v-if="!isGrid"
                                    class="w-12"
                                    style="background-color: #ffffff;"
                                    text
                                    bg
                                    @click="updateGridMode(true)">
                                    <el-icon>
                                        <span class="material-symbols-outlined mt-1">lists</span>
                                    </el-icon>
                                </el-button>

                                <el-button
                                    v-else
                                    class="w-12 hidden md:block"
                                    style="background-color: #ffffff;"
                                    text
                                    bg
                                    @click="updateGridMode(false)">
                                    <el-icon>
                                        <span class="material-symbols-outlined mt-1">grid_view</span>
                                    </el-icon>
                                </el-button>
                            </div>
                        </div>

                        <div class="flex justify-end hidden md:block xl:hidden">
                            <span class="text-sm mr-2 font-bold">アラート区分</span>
                            <div class="flex flex-wrap gap-x-2">
                                <div class="flex items-center">
                                    <div class="h-3 w-3 bg-[#E63415] rounded-sm"></div>
                                    <span class="ml-1 text-sm">1日以内</span>
                                </div>
                                <div class="flex items-center">
                                    <div class="h-3 w-3 bg-[#FF6600] rounded-sm"></div>
                                    <span class="ml-1 text-sm">1日前</span>
                                </div>
                                <div class="flex items-center">
                                    <div class="h-3 w-3 bg-[#FFDE0A] rounded-sm"></div>
                                    <span class="ml-1 text-sm">7日前</span>
                                </div>
                                <div class="flex items-center">
                                    <div class="h-3 w-3 bg-[#1EC79D] rounded-sm"></div>
                                    <span class="ml-1 text-sm">15日前</span>
                                </div>
                                <div class="flex items-center">
                                    <div class="h-3 w-3 bg-[#14CCCC] rounded-sm"></div>
                                    <span class="ml-1 text-sm">30日前</span>
                                </div>
                                <div class="flex items-center">
                                    <div class="h-3 w-3 bg-[#4167F0] rounded-sm"></div>
                                    <span class="ml-1 text-sm">在庫数</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </el-header>

        <el-mian style="padding-bottom: 20px;">
            <!-- 手机CARD部 -->
            <div class="md:hidden" v-loading="loading" style="height: calc(100vh - 192px)">
                <div class="custom-title flex flex-col">
                    <span>在庫一覧（在庫数　品名　有効期間）</span>
                    <span class="ml-auto">アイテム数: {{ mTotalRecords }}</span>
                </div>
                
                <div style="overflow-y: auto; height: calc(100vh - 242px)">
                <template v-if="(loginUserRole === ROLES.SYSTEM || (loginUserRole === ROLES.ADMIN && loginUserRoleIsHead)) && !searchDepartmentId">
                    <template v-if="!isGrid">
                        <el-card style="border: 1px solid #d1d5db; border-radius: 0px"
                            v-for="item in mTableData" :key="item.inventory_id">
                            <el-row align="middle">
                                <el-col :span="1">
                                    <span v-if="!item.isExpand" class="material-symbols-outlined cursor-pointer" @click="handleExpandButton(item)">
                                        <el-icon :size="20">
                                            <ArrowRight />
                                        </el-icon>
                                    </span>
                                    <span v-else class="material-symbols-outlined cursor-pointer" @click="handleCollapseButton(item)">
                                        <el-icon :size="20">
                                            <ArrowDown />
                                        </el-icon>
                                    </span>
                                </el-col>

                                <el-col :span="7" class="ml-2" align="middle">
                                    <span class="text-4xl">
                                        {{ item.quantity }}
                                    </span>
                                </el-col>

                                    <el-col :span="14" class="ml-2">
                                    <div v-if="loginUserRole === ROLES.SYSTEM">{{ item.organization_name }}</div>
                                    <div>{{ item.material_name }}</div>
                                    <div>{{ item.category_name }}</div>
                                </el-col>
                            </el-row>

                            <div v-if="item.isExpand" class="mt-2">
                                <span class="text-lg font-bold mb-2">詳細</span>
                                <el-card style="border: 1px solid #d1d5db; border-radius: 0px"
                                    v-for="detail in item.details" :key="detail.inventory_id">
                                    <el-row align="middle">
                                        <el-col :span="8" align="middle">
                                                <div v-if="!detail.quantity_is_click" class="w-full flex items-center justify-center bg-gray-100 border-2 border-gray-900 rounded-md cursor-pointer"
                                                    @click="handleClickQuantityButton(detail)">
                                                    <span class="text-3xl">
                                                {{ detail.quantity }}
                                            </span>
                                                </div>
                                        </el-col>

                                        <el-col :span="12" class="ml-2" @click="handleEditButton(detail, true)">
                                            <div>{{ detail.department_name }}</div>
                                            <span
                                                class="text-xl font-bold"
                                                :class="{ 'text-red-500': new Date(detail.expiration_date) < new Date() }">
                                                {{ moment(detail.expiration_date).format('YYYY-MM-DD') }}
                                            </span>
                                            <div class="flex">
                                                <div v-if="detail.check_result1 === 1" class="h-3 w-3 border-2 border-[#E63415] rounded-sm mr-1"></div>
                                                <div v-else-if="detail.check_result1 === 2" class="h-3 w-3 bg-[#E63415] rounded-sm mr-1"></div>
                                                <div v-else class="h-3 w-3 bg-gray-400 rounded-sm mr-1"></div>

                                                <div v-if="detail.check_result2 === 1" class="h-3 w-3 border-2 border-[#FF6600] rounded-sm mr-1"></div>
                                                <div v-else-if="detail.check_result2 === 2" class="h-3 w-3 bg-[#FF6600] rounded-sm mr-1"></div>
                                                <div v-else class="h-3 w-3 bg-gray-400 rounded-sm mr-1"></div>

                                                <div v-if="detail.check_result3 === 1" class="h-3 w-3 border-2 border-[#FFDE0A] rounded-sm mr-1"></div>
                                                <div v-else-if="detail.check_result3 === 2" class="h-3 w-3 bg-[#FFDE0A] rounded-sm mr-1"></div>
                                                <div v-else class="h-3 w-3 bg-gray-400 rounded-sm mr-1"></div>

                                                <div v-if="detail.check_result4 === 1" class="h-3 w-3 border-2 border-[#1EC79D] rounded-sm mr-1"></div>
                                                <div v-else-if="detail.check_result4 === 2" class="h-3 w-3 bg-[#1EC79D] rounded-sm mr-1"></div>
                                                <div v-else class="h-3 w-3 bg-gray-400 rounded-sm mr-1"></div>

                                                <div v-if="detail.check_result5 === 1" class="h-3 w-3 border-2 border-[#14CCCC] rounded-sm mr-1"></div>
                                                <div v-else-if="detail.check_result5 === 2" class="h-3 w-3 bg-[#14CCCC] rounded-sm mr-1"></div>
                                                <div v-else class="h-3 w-3 bg-gray-400 rounded-sm mr-1"></div>

                                                <div v-if="detail.check_result6 === 1" class="h-3 w-3 border-2 border-[#4167F0] rounded-sm mr-1"></div>
                                                <div v-else-if="detail.check_result6 === 2" class="h-3 w-3 bg-[#4167F0] rounded-sm mr-1"></div>
                                                <div v-else class="h-3 w-3 bg-gray-400 rounded-sm mr-1"></div>
                                            </div>
                                        </el-col>

                                        <el-col :span="2" class="ml-2" align="right">
                                            <span class="material-symbols-outlined cursor-pointer"
                                                @click="handleAlarmButton(detail)">
                                                alarm
                                            </span>
                                        </el-col>
                                    </el-row>
                                </el-card>
                            </div>
                        </el-card>
                    </template>
                    
                    <template v-else>
                        <div class="flex flex-wrap gap-4 p-4">
                            <el-card style="width: 100%; border: 2px solid #d1d5db; border-radius: 10px"
                                v-for="item in mTableData" :key="item.inventory_id">
                                <div v-if="loginUserRole === ROLES.SYSTEM">{{ item.organization_name }}</div>

                                <div class="flex flex-col justify-between">
                                    <div>{{ item.material_name }}</div>
                                        <div>{{ item.category_name }}</div>
                                </div>

                                    <el-row align="middle">
                                        <el-col :span="10" justify="center">
                                            <div>在庫合計</div>
                                            <div class="text-5xl">{{ item.quantity }}</div>
                                            <div class="flex justify-center">
                                                <span v-if="!item.isExpand" class="material-symbols-outlined cursor-pointer" @click="handleExpandButton(item)">
                                                    <el-icon :size="16">
                                                        <ArrowDown />
                                                    </el-icon>
                                                </span>
                                                <span v-else class="material-symbols-outlined cursor-pointer" @click="handleCollapseButton(item)">
                                                    <el-icon :size="16">
                                                        <ArrowUp />
                                                    </el-icon>
                                                </span>
                                            </div>
                                        </el-col>

                                        <el-col :span="14">
                                            <div class="h-[120px]" align="center">
                                                <ImgLoading v-if="item.images" v-model="item.images" class="carousel-image" />
                                                <div v-else class="w-full h-full bg-gray-100"></div>
                                            </div>
                                        </el-col>
                                    </el-row>
                                    
                                <div v-if="item.isExpand" class="mt-2">
                                    <span class="text-lg font-bold">詳細</span>
                                        <el-card class="mt-2" style="width: 100%; border: 1px solid #d1d5db; border-radius: 10px;"
                                        v-for="detail in item.details" :key="detail.inventory_id">

                                            <div class="flex justify-between">
                                                <div>{{ detail.department_name }}</div>
                                                <div>
                                                    <span class="material-symbols-outlined cursor-pointer"
                                                        @click="handleEditButton(detail)">
                                                        edit_square
                                                    </span>
                                                    <span class="material-symbols-outlined cursor-pointer ml-2"
                                                        @click="handleAlarmButton(detail)">
                                                        alarm
                                                    </span>
                                                    <span class="material-symbols-outlined cursor-pointer ml-2"
                                                        @click="handleDeleteButton(detail)">
                                                        delete
                                                    </span>
                                                </div>
                                            </div>

                                            <div class="flex justify-between items-center">
                                                <div class="w-1/2">
                                                    <div class="flex flex-col">
                                                        <div>
                                                            <span>期限</span>
                                                            <span class="ml-2" :class="{ 'text-red-500': new Date(detail.expiration_date) < new Date() }">
                                                                {{ moment(detail.expiration_date).format('YYYY-MM-DD') }}
                                                            </span>
                                                        </div>
                                                        <div class="flex">
                                                            <div v-if="detail.check_result1 === 1" class="h-3 w-3 border-2 border-[#E63415] rounded-sm mr-1"></div>
                                                            <div v-else-if="detail.check_result1 === 2" class="h-3 w-3 bg-[#E63415] rounded-sm mr-1"></div>
                                                            <div v-else class="h-3 w-3 bg-gray-400 rounded-sm mr-1"></div>

                                                            <div v-if="detail.check_result2 === 1" class="h-3 w-3 border-2 border-[#FF6600] rounded-sm mr-1"></div>
                                                            <div v-else-if="detail.check_result2 === 2" class="h-3 w-3 bg-[#FF6600] rounded-sm mr-1"></div>
                                                            <div v-else class="h-3 w-3 bg-gray-400 rounded-sm mr-1"></div>

                                                            <div v-if="detail.check_result3 === 1" class="h-3 w-3 border-2 border-[#FFDE0A] rounded-sm mr-1"></div>
                                                            <div v-else-if="detail.check_result3 === 2" class="h-3 w-3 bg-[#FFDE0A] rounded-sm mr-1"></div>
                                                            <div v-else class="h-3 w-3 bg-gray-400 rounded-sm mr-1"></div>

                                                            <div v-if="detail.check_result4 === 1" class="h-3 w-3 border-2 border-[#1EC79D] rounded-sm mr-1"></div>
                                                            <div v-else-if="detail.check_result4 === 2" class="h-3 w-3 bg-[#1EC79D] rounded-sm mr-1"></div>
                                                            <div v-else class="h-3 w-3 bg-gray-400 rounded-sm mr-1"></div>

                                                            <div v-if="detail.check_result5 === 1" class="h-3 w-3 border-2 border-[#14CCCC] rounded-sm mr-1"></div>
                                                            <div v-else-if="detail.check_result5 === 2" class="h-3 w-3 bg-[#14CCCC] rounded-sm mr-1"></div>
                                                            <div v-else class="h-3 w-3 bg-gray-400 rounded-sm mr-1"></div>

                                                            <div v-if="detail.check_result6 === 1" class="h-3 w-3 border-2 border-[#4167F0] rounded-sm mr-1"></div>
                                                            <div v-else-if="detail.check_result6 === 2" class="h-3 w-3 bg-[#4167F0] rounded-sm mr-1"></div>
                                                            <div v-else class="h-3 w-3 bg-gray-400 rounded-sm mr-1"></div>
                                                        </div>
                                                    </div>
                                                </div>
                                                <div class="flex flex-grow ml-2">
                                                    <div v-if="!detail.quantity_is_click" class="w-full flex items-center justify-center bg-gray-100 border-2 border-gray-900 rounded-md cursor-pointer"
                                                        @click="handleClickQuantityButton(detail)">
                                                        <span class="text-3xl">
                                                            {{ detail.quantity }}
                                                        </span>
                                                    </div>
                                                </div>
                                        </div>

                                        <div class="h-[160px] mt-2" align="center">
                                            <CarouselLoading v-if="detail.images" v-model="detail.images" height="160px" />
                                            <div v-else class="w-full h-full bg-gray-100"></div>
                                        </div>
                                    </el-card>
                                </div>
                            </el-card>
                        </div>
                    </template>
                </template>
                <template v-else>
                    <template v-if="!isGrid">
                        <el-card style="border: 1px solid #d1d5db; border-radius: 0px"
                            v-for="item in mTableData" :key="item.inventory_id">
                            <el-row align="middle">
                                <el-col :span="8" align="middle">
                                        <div v-if="!item.quantity_is_click" class="w-full flex items-center justify-center bg-gray-100 border-2 border-gray-900 rounded-md cursor-pointer"
                                            @click="handleClickQuantityButton(item)">
                                            <span class="text-5xl">
                                        {{ item.quantity }}
                                    </span>
                                        </div>
                                </el-col>

                                <el-col :span="12" class="ml-2" @click="handleEditButton(item, true)">
                                    <div>{{ item.material_name }}</div>
                                    <span
                                        class="text-xl font-bold"
                                        :class="{ 'text-red-500': new Date(item.expiration_date) < new Date() }">
                                        {{ moment(item.expiration_date).format('YYYY-MM-DD') }}
                                    </span>

                                    <div class="flex">
                                        <div v-if="item.check_result1 === 1" class="h-3 w-3 border-2 border-[#E63415] rounded-sm mr-1"></div>
                                        <div v-else-if="item.check_result1 === 2" class="h-3 w-3 bg-[#E63415] rounded-sm mr-1"></div>
                                        <div v-else class="h-3 w-3 bg-gray-400 rounded-sm mr-1"></div>

                                        <div v-if="item.check_result2 === 1" class="h-3 w-3 border-2 border-[#FF6600] rounded-sm mr-1"></div>
                                        <div v-else-if="item.check_result2 === 2" class="h-3 w-3 bg-[#FF6600] rounded-sm mr-1"></div>
                                        <div v-else class="h-3 w-3 bg-gray-400 rounded-sm mr-1"></div>

                                        <div v-if="item.check_result3 === 1" class="h-3 w-3 border-2 border-[#FFDE0A] rounded-sm mr-1"></div>
                                        <div v-else-if="item.check_result3 === 2" class="h-3 w-3 bg-[#FFDE0A] rounded-sm mr-1"></div>
                                        <div v-else class="h-3 w-3 bg-gray-400 rounded-sm mr-1"></div>

                                        <div v-if="item.check_result4 === 1" class="h-3 w-3 border-2 border-[#1EC79D] rounded-sm mr-1"></div>
                                        <div v-else-if="item.check_result4 === 2" class="h-3 w-3 bg-[#1EC79D] rounded-sm mr-1"></div>
                                        <div v-else class="h-3 w-3 bg-gray-400 rounded-sm mr-1"></div>

                                        <div v-if="item.check_result5 === 1" class="h-3 w-3 border-2 border-[#14CCCC] rounded-sm mr-1"></div>
                                        <div v-else-if="item.check_result5 === 2" class="h-3 w-3 bg-[#14CCCC] rounded-sm mr-1"></div>
                                        <div v-else class="h-3 w-3 bg-gray-400 rounded-sm mr-1"></div>

                                        <div v-if="item.check_result6 === 1" class="h-3 w-3 border-2 border-[#4167F0] rounded-sm mr-1"></div>
                                        <div v-else-if="item.check_result6 === 2" class="h-3 w-3 bg-[#4167F0] rounded-sm mr-1"></div>
                                        <div v-else class="h-3 w-3 bg-gray-400 rounded-sm mr-1"></div>
                                    </div>
                                </el-col>

                                <el-col :span="2" class="ml-2" align="right">
                                    <span class="material-symbols-outlined cursor-pointer"
                                        @click="handleAlarmButton(item)">
                                        alarm
                                    </span>
                                </el-col>
                            </el-row>
                        </el-card>
                    </template>
                    
                    <template v-else>
                        <div class="flex flex-wrap gap-4 p-4">
                                <el-card style="width: 100%; border: 2px solid #d1d5db; border-radius: 10px;"
                                v-for="item in mTableData" :key="item.inventory_id">

                                    <div class="flex justify-between">
                                        <div>{{ item.department_name }}</div>
                                        <div>
                                            <span class="material-symbols-outlined cursor-pointer"
                                                @click="handleEditButton(item)">
                                                edit_square
                                            </span>
                                            <span class="material-symbols-outlined cursor-pointer ml-2"
                                                @click="handleAlarmButton(item)">
                                                alarm
                                            </span>
                                            <span class="material-symbols-outlined cursor-pointer ml-2"
                                                @click="handleDeleteButton(item)">
                                                delete
                                            </span>
                                        </div>
                                    </div>

                                    <div class="flex flex-col justify-between">
                                        <div>{{ item.material_name }}</div>
                                        <div>{{ item.category_name }}</div>
                                    </div>

                                    <div class="flex justify-between items-center">
                                        <div class="w-1/2">
                                            <div class="flex flex-col">
                                                <div>
                                                    <span>期限</span>
                                                    <span class="ml-2" :class="{ 'text-red-500': new Date(item.expiration_date) < new Date() }">
                                                        {{ moment(item.expiration_date).format('YYYY-MM-DD') }}
                                                    </span>
                                                </div>
                                                <div class="flex">
                                                    <div v-if="item.check_result1 === 1" class="h-3 w-3 border-2 border-[#E63415] rounded-sm mr-1"></div>
                                                    <div v-else-if="item.check_result1 === 2" class="h-3 w-3 bg-[#E63415] rounded-sm mr-1"></div>
                                                    <div v-else class="h-3 w-3 bg-gray-400 rounded-sm mr-1"></div>

                                                    <div v-if="item.check_result2 === 1" class="h-3 w-3 border-2 border-[#FF6600] rounded-sm mr-1"></div>
                                                    <div v-else-if="item.check_result2 === 2" class="h-3 w-3 bg-[#FF6600] rounded-sm mr-1"></div>
                                                    <div v-else class="h-3 w-3 bg-gray-400 rounded-sm mr-1"></div>

                                                    <div v-if="item.check_result3 === 1" class="h-3 w-3 border-2 border-[#FFDE0A] rounded-sm mr-1"></div>
                                                    <div v-else-if="item.check_result3 === 2" class="h-3 w-3 bg-[#FFDE0A] rounded-sm mr-1"></div>
                                                    <div v-else class="h-3 w-3 bg-gray-400 rounded-sm mr-1"></div>

                                                    <div v-if="item.check_result4 === 1" class="h-3 w-3 border-2 border-[#1EC79D] rounded-sm mr-1"></div>
                                                    <div v-else-if="item.check_result4 === 2" class="h-3 w-3 bg-[#1EC79D] rounded-sm mr-1"></div>
                                                    <div v-else class="h-3 w-3 bg-gray-400 rounded-sm mr-1"></div>

                                                    <div v-if="item.check_result5 === 1" class="h-3 w-3 border-2 border-[#14CCCC] rounded-sm mr-1"></div>
                                                    <div v-else-if="item.check_result5 === 2" class="h-3 w-3 bg-[#14CCCC] rounded-sm mr-1"></div>
                                                    <div v-else class="h-3 w-3 bg-gray-400 rounded-sm mr-1"></div>

                                                    <div v-if="item.check_result6 === 1" class="h-3 w-3 border-2 border-[#4167F0] rounded-sm mr-1"></div>
                                                    <div v-else-if="item.check_result6 === 2" class="h-3 w-3 bg-[#4167F0] rounded-sm mr-1"></div>
                                                    <div v-else class="h-3 w-3 bg-gray-400 rounded-sm mr-1"></div>
                                                </div>
                                            </div>
                                        </div>
                                        <div class="flex flex-grow ml-2">
                                            <div v-if="!item.quantity_is_click" class="w-full flex items-center justify-center bg-gray-100 border-2 border-gray-900 rounded-md cursor-pointer"
                                                @click="handleClickQuantityButton(item)">
                                                <span class="text-3xl">
                                                    {{ item.quantity }}
                                                </span>
                                            </div>
                                        </div>
                                </div>

                                <div class="h-[160px] mt-2" align="center">
                                    <CarouselLoading v-if="item.images" v-model="item.images" height="160px" />
                                    <div v-else class="w-full h-full bg-gray-100"></div>
                                </div>
                            </el-card>
                        </div>
                    </template>
                </template>
                </div>
            </div>

            <!-- WEB表格部 -->
            <div class="hidden md:block w-full">
                <el-table v-if="!isGrid" :data="mTableData" height="calc(100vh - 260px)" v-loading="loading">
                    <template v-if="(loginUserRole === ROLES.SYSTEM || (loginUserRole === ROLES.ADMIN && loginUserRoleIsHead)) && !searchDepartmentId">
                        <el-table-column type="expand" label="在庫一覧" width="80" align="center" row-key="id">
                            <template #default="props">
                                <div class="mx-12">
                                    <el-table :data="props.row.details" :header-cell-style="{ backgroundColor: '#ffffff', color: '#000000' }">
                                        <el-table-column label="詳細" width="80" align="center">
                                            <template #default="scope">
                                                <el-button
                                                    class="w-12"
                                                    type="info"
                                                    style="background-color: #b7b7b7;"
                                                    text
                                                    bg
                                                    @click="handleEditButton(scope.row)">
                                                    <span>詳細</span>
                                                </el-button>
                                            </template>
                                        </el-table-column>
                                        <el-table-column prop="department_name" label="所属部門" width="160" />
                                        <el-table-column prop="storage_date" label="保管日" width="100" align="center">
                                            <template #default="{ row }">
                                                <span>{{ moment(row.storage_date).format('YYYY-MM-DD') }}</span>
                                            </template>
                                        </el-table-column>
                                        <el-table-column prop="expiration_date" label="有効期限" width="100" align="center">
                                            <template #default="{ row }">
                                                <span
                                                    :class="{ 'text-red-500 bg-gray-200': new Date(row.expiration_date) < new Date() }">
                                                    {{ moment(row.expiration_date).format('YYYY-MM-DD') }}
                                                </span>
                                            </template>
                                        </el-table-column>
                                        <el-table-column prop="quantity" label="在庫数" width="66" header-align="center" align="right" />
                                        <el-table-column prop="" label="画像" width="130">
                                            <template #default="scope">
                                                <el-row align="middle">
                                                    <template v-if="scope.row.images">
                                                        <template v-for="image in getImgUrls(scope.row.images)" :key="image">
                                                            <ImgLoading :model-value="image" class="thumbnail mr-1" />
                                                        </template>
                                                    </template>
                                                </el-row>
                                            </template>
                                        </el-table-column>
                                        <el-table-column prop="check_result" label="アラート状況" width="110" align="center">
                                            <template #default="{ row }">
                                                <div class="flex">
                                                    <div v-if="row.check_result1 === 1" class="h-3 w-3 border-2 border-[#E63415] rounded-sm mr-1"></div>
                                                    <div v-else-if="row.check_result1 === 2" class="h-3 w-3 bg-[#E63415] rounded-sm mr-1"></div>
                                                    <div v-else class="h-3 w-3 bg-gray-400 rounded-sm mr-1"></div>

                                                    <div v-if="row.check_result2 === 1" class="h-3 w-3 border-2 border-[#FF6600] rounded-sm mr-1"></div>
                                                    <div v-else-if="row.check_result2 === 2" class="h-3 w-3 bg-[#FF6600] rounded-sm mr-1"></div>
                                                    <div v-else class="h-3 w-3 bg-gray-400 rounded-sm mr-1"></div>

                                                    <div v-if="row.check_result3 === 1" class="h-3 w-3 border-2 border-[#FFDE0A] rounded-sm mr-1"></div>
                                                    <div v-else-if="row.check_result3 === 2" class="h-3 w-3 bg-[#FFDE0A] rounded-sm mr-1"></div>
                                                    <div v-else class="h-3 w-3 bg-gray-400 rounded-sm mr-1"></div>

                                                    <div v-if="row.check_result4 === 1" class="h-3 w-3 border-2 border-[#1EC79D] rounded-sm mr-1"></div>
                                                    <div v-else-if="row.check_result4 === 2" class="h-3 w-3 bg-[#1EC79D] rounded-sm mr-1"></div>
                                                    <div v-else class="h-3 w-3 bg-gray-400 rounded-sm mr-1"></div>

                                                    <div v-if="row.check_result5 === 1" class="h-3 w-3 border-2 border-[#14CCCC] rounded-sm mr-1"></div>
                                                    <div v-else-if="row.check_result5 === 2" class="h-3 w-3 bg-[#14CCCC] rounded-sm mr-1"></div>
                                                    <div v-else class="h-3 w-3 bg-gray-400 rounded-sm mr-1"></div>

                                                    <div v-if="row.check_result6 === 1" class="h-3 w-3 border-2 border-[#4167F0] rounded-sm mr-1"></div>
                                                    <div v-else-if="row.check_result6 === 2" class="h-3 w-3 bg-[#4167F0] rounded-sm mr-1"></div>
                                                    <div v-else class="h-3 w-3 bg-gray-400 rounded-sm mr-1"></div>
                                                </div>
                                            </template>
                                        </el-table-column>
                                        <el-table-column label="アラート" width="80" align="center">
                                            <template #default="scope">
                                                <span class="material-symbols-outlined cursor-pointer mt-2"
                                                    @click="handleAlarmButton(scope.row)">
                                                    alarm
                                                </span>
                                            </template>
                                        </el-table-column>
                                        <el-table-column label="削除" width="52" align="center">
                                            <template #default="scope">
                                                <span class="material-symbols-outlined cursor-pointer mt-2"
                                                    @click="handleDeleteButton(scope.row)">
                                                    delete
                                                </span>
                                            </template>
                                        </el-table-column>
                                    </el-table>
                                </div>
                            </template>
                        </el-table-column>
                        <el-table-column v-if="loginUserRole === ROLES.SYSTEM" prop="organization_name" label="所属組織" width="200" />
                        <el-table-column prop="category_name" label="カテゴリ" width="160" />
                        <el-table-column prop="material_name" label="アイテム名" min-width="400">
                            <template #default="scope">
                                {{ scope.row.material_name }}<br />
                                <span v-if="scope.row.jancode" class="text-sm text-gray-500">
                                    （{{ scope.row.jancode }}）
                                </span>
                            </template>
                        </el-table-column>
                        <el-table-column prop="quantity" label="在庫数" width="66" header-align="center" align="right" />
                        <el-table-column prop="" label="画像" width="52" align="center">
                            <template #default="scope">
                                <ImgLoading v-model="scope.row.images" class="thumbnail" />
                            </template>
                        </el-table-column>
                    </template>

                    <template v-else>
                        <el-table-column label="在庫一覧" width="80" align="center">
                            <template #default="scope">
                                <el-button
                                    class="w-12"
                                    type="info"
                                    style="background-color: #b7b7b7;"
                                    text
                                    bg
                                    @click="handleEditButton(scope.row)">
                                    <span>詳細</span>
                                </el-button>
                            </template>
                        </el-table-column>
                        <el-table-column v-if="searchDepartmentId" prop="organization_name" label="所属組織" width="200" />
                        <el-table-column v-if="searchDepartmentId" prop="department_name" label="所属部門" width="160" />
                        <el-table-column prop="category_name" label="カテゴリ" width="160" />
                        <el-table-column prop="material_name" label="アイテム名" min-width="400">
                            <template #default="scope">
                                {{ scope.row.material_name }}<br />
                                <span v-if="scope.row.jancode" class="text-sm text-gray-500">
                                    （{{ scope.row.jancode }}）
                                </span>
                            </template>
                        </el-table-column>
                        <el-table-column prop="storage_date" label="保管日" width="100" align="center">
                            <template #default="{ row }">
                                <span>{{ moment(row.storage_date).format('YYYY-MM-DD') }}</span>
                            </template>
                        </el-table-column>
                        <el-table-column prop="expiration_date" label="有効期限" width="100" align="center">
                            <template #default="{ row }">
                                <span
                                    :class="{ 'text-red-500 bg-gray-200': new Date(row.expiration_date) < new Date() }">
                                    {{ moment(row.expiration_date).format('YYYY-MM-DD') }}
                                </span>
                            </template>
                        </el-table-column>
                        <el-table-column prop="quantity" label="在庫数" width="66" header-align="center" align="right" />
                        <el-table-column prop="" label="画像" width="130">
                            <template #default="scope">
                                <el-row align="middle">
                                    <template v-if="scope.row.images">
                                        <template v-for="image in getImgUrls(scope.row.images)" :key="image">
                                            <ImgLoading :model-value="image" class="thumbnail mr-1" />
                                        </template>
                                    </template>
                                </el-row>
                            </template>
                        </el-table-column>
                        <el-table-column prop="check_result" label="アラート状況" width="110" align="center">
                            <template #default="{ row }">
                                <div class="flex">
                                    <div v-if="row.check_result1 === 1" class="h-3 w-3 border-2 border-[#E63415] rounded-sm mr-1"></div>
                                    <div v-else-if="row.check_result1 === 2" class="h-3 w-3 bg-[#E63415] rounded-sm mr-1"></div>
                                    <div v-else class="h-3 w-3 bg-gray-400 rounded-sm mr-1"></div>

                                    <div v-if="row.check_result2 === 1" class="h-3 w-3 border-2 border-[#FF6600] rounded-sm mr-1"></div>
                                    <div v-else-if="row.check_result2 === 2" class="h-3 w-3 bg-[#FF6600] rounded-sm mr-1"></div>
                                    <div v-else class="h-3 w-3 bg-gray-400 rounded-sm mr-1"></div>

                                    <div v-if="row.check_result3 === 1" class="h-3 w-3 border-2 border-[#FFDE0A] rounded-sm mr-1"></div>
                                    <div v-else-if="row.check_result3 === 2" class="h-3 w-3 bg-[#FFDE0A] rounded-sm mr-1"></div>
                                    <div v-else class="h-3 w-3 bg-gray-400 rounded-sm mr-1"></div>

                                    <div v-if="row.check_result4 === 1" class="h-3 w-3 border-2 border-[#1EC79D] rounded-sm mr-1"></div>
                                    <div v-else-if="row.check_result4 === 2" class="h-3 w-3 bg-[#1EC79D] rounded-sm mr-1"></div>
                                    <div v-else class="h-3 w-3 bg-gray-400 rounded-sm mr-1"></div>

                                    <div v-if="row.check_result5 === 1" class="h-3 w-3 border-2 border-[#14CCCC] rounded-sm mr-1"></div>
                                    <div v-else-if="row.check_result5 === 2" class="h-3 w-3 bg-[#14CCCC] rounded-sm mr-1"></div>
                                    <div v-else class="h-3 w-3 bg-gray-400 rounded-sm mr-1"></div>

                                    <div v-if="row.check_result6 === 1" class="h-3 w-3 border-2 border-[#4167F0] rounded-sm mr-1"></div>
                                    <div v-else-if="row.check_result6 === 2" class="h-3 w-3 bg-[#4167F0] rounded-sm mr-1"></div>
                                    <div v-else class="h-3 w-3 bg-gray-400 rounded-sm mr-1"></div>
                                </div>
                            </template>
                        </el-table-column>
                        <el-table-column label="アラート" width="80" align="center">
                            <template #default="scope">
                                <span class="material-symbols-outlined cursor-pointer mt-2"
                                    @click="handleAlarmButton(scope.row)">
                                    alarm
                                </span>
                            </template>
                        </el-table-column>
                        <el-table-column label="削除" width="52" align="center">
                            <template #default="scope">
                                <span class="material-symbols-outlined cursor-pointer mt-2"
                                    @click="handleDeleteButton(scope.row)">
                                    delete
                                </span>
                            </template>
                        </el-table-column>
                    </template>
                </el-table>

                <div v-else v-loading="loading" style="height: calc(100vh - 260px)">
                    <div class="custom-title flex items-center" style="height: 40px;">
                        <span>在庫一覧（在庫数　品名　有効期間）</span>
                        <span></span>
                    </div>

                    <div style="height: calc(100vh - 270px); overflow-y: auto;">
                        <div class="flex flex-wrap gap-4 p-4">
                            <template v-if="(loginUserRole === ROLES.SYSTEM || (loginUserRole === ROLES.ADMIN && loginUserRoleIsHead)) && !searchDepartmentId">
                                <el-card style="width: 292px; border: 2px solid #d1d5db; border-radius: 10px"
                                    v-for="item in mTableData" :key="item.inventory_id">
                                    <div v-if="loginUserRole === ROLES.SYSTEM">{{ item.organization_name }}</div>

                                    <div class="h-[100px] flex flex-col justify-between">
                                        <div>{{ item.material_name }}</div>
                                            <div>{{ item.category_name }}</div>
                                    </div>

                                    <el-row align="middle">
                                        <el-col :span="10" justify="center">
                                            <div>在庫合計</div>
                                            <div class="text-4xl">{{ item.quantity }}</div>
                                            <div class="flex justify-center">
                                                <span v-if="!item.isExpand" class="material-symbols-outlined cursor-pointer" @click="handleExpandButton(item)">
                                                    <el-icon :size="16">
                                                        <ArrowDown />
                                                    </el-icon>
                                                </span>
                                                <span v-else class="material-symbols-outlined cursor-pointer" @click="handleCollapseButton(item)">
                                                    <el-icon :size="16">
                                                        <ArrowUp />
                                                    </el-icon>
                                                </span>
                                            </div>
                                        </el-col>

                                        <el-col :span="14">
                                            <div class="h-[100px]" align="center">
                                                <ImgLoading v-if="item.images" v-model="item.images" class="carousel-image" />
                                                <div v-else class="w-full h-full bg-gray-100"></div>
                                            </div>
                                        </el-col>
                                    </el-row>
                                        
                                    <div v-if="item.isExpand" class="mt-2">
                                        <span class="text-lg font-bold">詳細</span>
                                        <el-card class="mt-2" style="width: 250px; border: 1px solid #d1d5db; border-radius: 10px;"
                                            v-for="detail in item.details" :key="detail.inventory_id">

                                            <div class="flex justify-between">
                                                <div>{{ detail.department_name }}</div>
                                                <div>
                                                    <span class="material-symbols-outlined cursor-pointer"
                                                        @click="handleEditButton(detail)">
                                                        edit_square
                                                        </span>
                                                        <span class="material-symbols-outlined cursor-pointer ml-2"
                                                            @click="handleAlarmButton(detail)">
                                                            alarm
                                                        </span>
                                                    <span class="material-symbols-outlined cursor-pointer ml-2"
                                                        @click="handleDeleteButton(detail)">
                                                        delete
                                                    </span>
                                                </div>
                                            </div>

                                            <div class="flex justify-between items-center">
                                                <div class="flex flex-col">
                                                    <div>
                                                        <span>期限</span>
                                                        <span class="ml-2" :class="{ 'text-red-500': new Date(detail.expiration_date) < new Date() }">
                                                            {{ moment(detail.expiration_date).format('YYYY-MM-DD') }}
                                                        </span>
                                                    </div>
                                                    <div class="flex">
                                                        <div v-if="detail.check_result1 === 1" class="h-3 w-3 border-2 border-[#E63415] rounded-sm mr-1"></div>
                                                        <div v-else-if="detail.check_result1 === 2" class="h-3 w-3 bg-[#E63415] rounded-sm mr-1"></div>
                                                        <div v-else class="h-3 w-3 bg-gray-400 rounded-sm mr-1"></div>

                                                        <div v-if="detail.check_result2 === 1" class="h-3 w-3 border-2 border-[#FF6600] rounded-sm mr-1"></div>
                                                        <div v-else-if="detail.check_result2 === 2" class="h-3 w-3 bg-[#FF6600] rounded-sm mr-1"></div>
                                                        <div v-else class="h-3 w-3 bg-gray-400 rounded-sm mr-1"></div>

                                                        <div v-if="detail.check_result3 === 1" class="h-3 w-3 border-2 border-[#FFDE0A] rounded-sm mr-1"></div>
                                                        <div v-else-if="detail.check_result3 === 2" class="h-3 w-3 bg-[#FFDE0A] rounded-sm mr-1"></div>
                                                        <div v-else class="h-3 w-3 bg-gray-400 rounded-sm mr-1"></div>

                                                        <div v-if="detail.check_result4 === 1" class="h-3 w-3 border-2 border-[#1EC79D] rounded-sm mr-1"></div>
                                                        <div v-else-if="detail.check_result4 === 2" class="h-3 w-3 bg-[#1EC79D] rounded-sm mr-1"></div>
                                                        <div v-else class="h-3 w-3 bg-gray-400 rounded-sm mr-1"></div>

                                                        <div v-if="detail.check_result5 === 1" class="h-3 w-3 border-2 border-[#14CCCC] rounded-sm mr-1"></div>
                                                        <div v-else-if="detail.check_result5 === 2" class="h-3 w-3 bg-[#14CCCC] rounded-sm mr-1"></div>
                                                        <div v-else class="h-3 w-3 bg-gray-400 rounded-sm mr-1"></div>

                                                        <div v-if="detail.check_result6 === 1" class="h-3 w-3 border-2 border-[#4167F0] rounded-sm mr-1"></div>
                                                        <div v-else-if="detail.check_result6 === 2" class="h-3 w-3 bg-[#4167F0] rounded-sm mr-1"></div>
                                                        <div v-else class="h-3 w-3 bg-gray-400 rounded-sm mr-1"></div>
                                                    </div>
                                                </div>
                                                <div class="flex flex-grow ml-2">
                                                    <div v-if="!detail.quantity_is_click" class="w-full flex items-center justify-center bg-gray-100 border-2 border-gray-900 rounded-md cursor-pointer"
                                                        @click="handleClickQuantityButtonWebMode(detail)">
                                                        <span class="text-3xl">
                                                            {{ detail.quantity }}
                                                        </span>
                                                    </div>
                                                    <div v-else class="w-full quantity-editor">
                                                        <el-input-number 
                                                            :ref="(el: any) => setInputRef(detail.inventory_id, el)" 
                                                            class="w-full big-font-input" 
                                                            size="large" 
                                                            v-model="detail.quantity" 
                                                            :min="0" 
                                                            :max="999999" 
                                                            controls-position="right" 
                                                            @blur="handleQuantityBlur(detail)"
                                                            @focus="handleQuantityFocus(detail)"
                                                        />
                                                    </div>

                                                </div>
                                            </div>

                                            <div class="h-[160px] mt-2" align="center">
                                                <CarouselLoading v-if="detail.images" v-model="detail.images" height="160px" />
                                                <div v-else class="w-full h-full bg-gray-100"></div>
                                            </div>
                                        </el-card>
                                    </div>
                                </el-card>
                            </template>
                            
                            <template v-else>
                                <el-card style="width: 250px; border: 2px solid #d1d5db; border-radius: 10px;"
                                    v-for="item in mTableData" :key="item.inventory_id">
                                    <div class="flex justify-between">
                                        <div>{{ item.department_name }}</div>
                                        <div>
                                            <span class="material-symbols-outlined cursor-pointer"
                                                @click="handleEditButton(item)">
                                                edit_square
                                            </span>
                                            <span class="material-symbols-outlined cursor-pointer ml-2"
                                                @click="handleAlarmButton(item)">
                                                alarm
                                            </span>
                                            <span class="material-symbols-outlined cursor-pointer ml-2"
                                                @click="handleDeleteButton(item)">
                                                delete
                                            </span>
                                        </div>
                                    </div>

                                    <div class="h-[100px] flex flex-col justify-between">
                                        <div>{{ item.material_name }}</div>
                                        <div>{{ item.category_name }}</div>
                                    </div>

                                    <div class="flex justify-between items-center">
                                        <div class="flex flex-col">
                                            <div>
                                                <span>期限</span>
                                                <span class="ml-2" :class="{ 'text-red-500': new Date(item.expiration_date) < new Date() }">
                                                    {{ moment(item.expiration_date).format('YYYY-MM-DD') }}
                                                </span>
                                            </div>
                                            <div class="flex">
                                                <div v-if="item.check_result1 === 1" class="h-3 w-3 border-2 border-[#E63415] rounded-sm mr-1"></div>
                                                <div v-else-if="item.check_result1 === 2" class="h-3 w-3 bg-[#E63415] rounded-sm mr-1"></div>
                                                <div v-else class="h-3 w-3 bg-gray-400 rounded-sm mr-1"></div>

                                                <div v-if="item.check_result2 === 1" class="h-3 w-3 border-2 border-[#FF6600] rounded-sm mr-1"></div>
                                                <div v-else-if="item.check_result2 === 2" class="h-3 w-3 bg-[#FF6600] rounded-sm mr-1"></div>
                                                <div v-else class="h-3 w-3 bg-gray-400 rounded-sm mr-1"></div>

                                                <div v-if="item.check_result3 === 1" class="h-3 w-3 border-2 border-[#FFDE0A] rounded-sm mr-1"></div>
                                                <div v-else-if="item.check_result3 === 2" class="h-3 w-3 bg-[#FFDE0A] rounded-sm mr-1"></div>
                                                <div v-else class="h-3 w-3 bg-gray-400 rounded-sm mr-1"></div>

                                                <div v-if="item.check_result4 === 1" class="h-3 w-3 border-2 border-[#1EC79D] rounded-sm mr-1"></div>
                                                <div v-else-if="item.check_result4 === 2" class="h-3 w-3 bg-[#1EC79D] rounded-sm mr-1"></div>
                                                <div v-else class="h-3 w-3 bg-gray-400 rounded-sm mr-1"></div>

                                                <div v-if="item.check_result5 === 1" class="h-3 w-3 border-2 border-[#14CCCC] rounded-sm mr-1"></div>
                                                <div v-else-if="item.check_result5 === 2" class="h-3 w-3 bg-[#14CCCC] rounded-sm mr-1"></div>
                                                <div v-else class="h-3 w-3 bg-gray-400 rounded-sm mr-1"></div>

                                                <div v-if="item.check_result6 === 1" class="h-3 w-3 border-2 border-[#4167F0] rounded-sm mr-1"></div>
                                                <div v-else-if="item.check_result6 === 2" class="h-3 w-3 bg-[#4167F0] rounded-sm mr-1"></div>
                                                <div v-else class="h-3 w-3 bg-gray-400 rounded-sm mr-1"></div>
                                            </div>
                                        </div>
                                        <div class="flex flex-grow ml-2">
                                            <div v-if="!item.quantity_is_click" class="w-full flex items-center justify-center bg-gray-100 border-2 border-gray-900 rounded-md cursor-pointer"
                                                @click="handleClickQuantityButtonWebMode(item)">
                                                <span class="text-3xl">
                                                    {{ item.quantity }}
                                                </span>
                                            </div>
                                            <div v-else class="w-full quantity-editor">
                                                <el-input-number 
                                                    :ref="(el: any) => setInputRef(item.inventory_id, el)" 
                                                    class="w-full big-font-input" 
                                                    size="large" 
                                                    v-model="item.quantity" 
                                                    :min="0" 
                                                    :max="999999" 
                                                    controls-position="right" 
                                                    @blur="handleQuantityBlur(item)"
                                                    @focus="handleQuantityFocus(item)"
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    <div class="h-[160px] mt-2" align="center">
                                        <CarouselLoading v-if="item.images" v-model="item.images" height="160px" />
                                        <div v-else class="w-full h-full bg-gray-100"></div>
                                    </div>
                                </el-card>
                            </template>
                        </div>
                    </div>
                </div>
            </div>

            <!-- 分页部 -->
            <div class="flex w-full justify-end mt-4">
                <div class="hidden md:block mr-3">
                    <el-pagination v-model:current-page="mCurrentPage" v-model:page-size="mPageSize"
                        :page-sizes="[10, 20, 50, 100]" layout="total, sizes, prev, pager, next, jumper"
                        :total="mTotalRecords" @size-change="handleSizeChange" @current-change="handleCurrentChange" />
                </div>

                <div class="md:hidden">
                    <el-pagination layout="prev, pager, next" :total="mTotalRecords"
                    @current-change="handleCurrentChange" />
                </div>
            </div>
        </el-mian>
    </el-container>

    <!-- 添加/编辑对话框 -->
    <el-dialog class="custom-dialog" modal-class="custom-dialog-modal" style="max-width:800px"
        v-model="dialogFormVisible" :title="form.add_update === 'add' ? '新規登録' : '詳細'" align-center>
        <div class="flex-col w-full px-2 justify-center items-center">
            <!-- 右侧表单 -->
            <el-form :model="form" :rules="rules" ref="formRef">
                
                <!-- Add v-if to only show this section when adding, not editing -->
                <div class="w-full md:px-2" v-if="form.add_update === 'add'">
                    <el-form-item label="JANコード" prop="jancode" label-width="100px" :error="errors.jancode">
                        <el-input v-model="form.jancode" autocomplete="off" placeholder="JANコード">
                            <template #append>
                                <el-button style="background-color: rgb(76, 175, 80); 
                                               color: white; 
                                               border: none;
                                               height: 100%;
                                               margin: 0;
                                               padding: 0 8px;
                                               border-right: 1px solid #dcdfe6;" @click="handleScanStart">
                                    スキャンを開始</el-button>
                                <el-button style="background-color: rgb(21, 96, 130); 
                                               color: white; 
                                               border: none;
                                               height: 100%;
                                               margin: 0;
                                               padding: 0 8px;
                                               border-radius: 0 4px 4px 0;"
                                    @click="handleJancodeSearch(form.jancode || '')">検索</el-button>
                            </template>
                        </el-input>
                    </el-form-item>
                </div>

                <div class="flex flex-col md:flex-row">
                    <div class="w-full md:w-1/2 md:px-2">
                        <el-form-item v-if="loginUserRole === ROLES.SYSTEM" label="組織" prop="organization_id"
                            label-width="100px">
                            <OrganizationSelect v-model="form.organization_id" :disabled="form.add_update === 'update'"
                                @organization-name-change="form.organization_name = $event" placeholder="組織を選択" />
                        </el-form-item>
                        <el-form-item v-if="loginUserRole === ROLES.SYSTEM || (loginUserRole === ROLES.ADMIN && loginUserRoleIsHead)" label="部門" prop="department_id" label-width="100px">
                            <DepartmentSelect :organization-id="form.organization_id" v-model="form.department_id" :disabled="form.add_update === 'update'"
                                @department-name-change="form.department_name = $event" placeholder="部門を選択" />
                        </el-form-item>
                        <el-form-item label="カテゴリ" prop="category_id" label-width="100px">
                            <CategorySelect :organization-id="form.organization_id" v-model="form.category_id" :disabled="form.add_update === 'update'"
                                @category-name-change="form.category_name = $event" placeholder="カテゴリを選択" />
                        </el-form-item>
                        <el-form-item label="アイテム名" prop="material_code" label-width="100px">
                            <MaterialSelect :organization-id="form.organization_id" :category-id="form.category_id" v-model="form.material_code" :disabled="form.add_update === 'update'"
                                @material-name-change="form.material_name = $event" 
                                @update:organization-id="updateOrganizationId"
                                @update:category-id="updateCategoryId"
                                @update:expiration-day="updateExpirationDate"
                                placeholder="アイテム名を選択" />
                        </el-form-item>
                        <el-form-item label="在庫数" prop="quantity" label-width="100px">
                            <el-input-number v-model="form.quantity" :min="0" :max="999999" />
                        </el-form-item>

                    </div>
                    <div class="w-full md:w-1/2 md:px-2">
                        <el-form-item label="保管日" prop="storage_date" label-width="100px">
                            <el-date-picker v-model="form.storage_date" type="date" placeholder="保管日を選択" />
                        </el-form-item>
                        <el-form-item label="有効期限" prop="expiration_date" label-width="100px">
                            <el-date-picker v-model="form.expiration_date" type="date" placeholder="有効期限を選択" />
                        </el-form-item>
                        <el-form-item label="保管場所" prop="location" label-width="100px">
                            <el-input v-model="form.location" autocomplete="off" placeholder="保管場所を入力" />
                        </el-form-item>
                        <el-form-item label="コメント" prop="comment" label-width="100px">
                            <el-input v-model="form.comment" autocomplete="off" placeholder="コメントを入力" />
                        </el-form-item>

                    </div>
                </div>
            </el-form>

            <div class="flex w-full items-center justify-center">
                <el-upload action="#" list-type="picture-card" :auto-upload="false" :on-change="handleImageChange" multiple
                    :on-remove="handleRemove" :limit="3" :on-exceed="handleExceed" :file-list="fileList" accept=".jpg,.jpeg,.png,.webp,.gif">
                    <el-icon>
                        <Plus />
                    </el-icon>
                    <template #file="{ file }">
                        <div class="w-full">
                            <img class="el-upload-list__item-thumbnail" :src="file.url" alt="" />
                            <span class="el-upload-list__item-actions">
                                <span class="el-upload-list__item-preview" @click="handlePictureCardPreview(file)">
                                    <el-icon><zoom-in /></el-icon>
                                </span>
                                <span v-if="!disabled" class="el-upload-list__item-delete"
                                    @click="handleRemove(file)">
                                    <el-icon>
                                        <Delete />
                                    </el-icon>
                                </span>
                            </span>
                        </div>
                    </template>
                </el-upload>
            </div>

        </div>

        <div class="flex flex-col w-full">
            <div class="barcode-scanner-container" v-if="barcodeScannerState">
                <BarcodeScanner 
                    ref="barcodeScannerRef" 
                    @code-scanned="handleScanSuccess" 
                    @scan-stopped="barcodeScannerState = false" 
                />
            </div>
        </div>

        <template #footer>
            <div class="dialog-footer flex items-center">
                <el-button 
                    v-if="form.add_update === 'update' && dialogFormShowDel"
                    type="danger"
                    @click="handleDeleteButton2(form.inventory_id, form.sk)">
                    削除
                </el-button>
                <div class="flex gap-2 ml-auto">
                    <el-button @click="dialogFormVisible = false">キャンセル</el-button>
                    <el-button type="primary" @click="handleAddUpdateSubmit(formRef)">
                        {{ form.add_update === 'add' ? '登録' : '更新' }}
                    </el-button>
                </div>
            </div>
        </template>
    </el-dialog>

    <!-- 画像预览 -->
    <el-dialog class="custom-dialog" modal-class="custom-dialog-modal" style="max-width:800px"
        v-model="dialogVisible" align-center>
        <div class="flex justify-center items-center">
            <img w-full :src="dialogImageUrl" alt="Preview Image" />
        </div>
    </el-dialog>

    <!-- アラート設定 -->
    <el-dialog class="custom-dialog" modal-class="custom-dialog-modal" v-model="dialogAlarmVisible"
        :title="form.add_update === 'add' ? '新規登録' : '詳細'" align-center>
        <el-form :model="alarmForm" :rules="alarmRules" ref="alarmFormRef">
            <el-form-item label="責任者">
                <UserSelect :organization-id="alarmForm.organization_id" v-model="alarmForm.responsible_person"
                    placeholder="責任者を選択" />
            </el-form-item>
            <el-form-item label="アラート設定">
                <el-checkbox-group v-model="alarmForm.alarm_setting">
                    <el-checkbox label="1日以内">1日以内</el-checkbox>
                    <el-checkbox label="1日前">1日前</el-checkbox>
                    <el-checkbox label="7日前">7日前</el-checkbox>
                    <el-checkbox label="15日前">15日前</el-checkbox>
                    <el-checkbox label="30日前">30日前</el-checkbox>
                    <el-checkbox label="在庫数">在庫数</el-checkbox>
                </el-checkbox-group>
            </el-form-item>
            <el-form-item label="在庫数" v-if="alarmForm.alarm_setting.includes('在庫数')">
                <el-input-number v-model="alarmForm.quantity" :min="0" :max="999999" />
            </el-form-item>
            <el-form-item label="有効">
                <el-switch v-model="alarmForm.is_actived" active-value="1" inactive-value="0" />
            </el-form-item>
        </el-form>
        <template #footer>
            <div class="dialog-footer flex items-center">
                <el-button 
                    v-if="alarmForm.add_update === 'update'"
                    type="danger" 
                    @click="handleDeleteReminderSetting">
                    削除
                </el-button>
                <div class="flex gap-2 ml-auto">
                    <el-button @click="dialogAlarmVisible = false">キャンセル</el-button>
                    <el-button type="primary" @click="handleAlarmSubmit(alarmFormRef)">
                        {{ alarmForm.add_update === 'add' ? '登録' : '更新' }}
                    </el-button>
                </div>
            </div>
        </template>
    </el-dialog>

    <!-- Quantity Edit Drawer -->
    <QuantityEditDrawer
        v-model="quantityDrawerVisible"
        :quantity="editingItem?.quantity || 0"
        @confirm="handleQuantityConfirm"
        @cancel="handleQuantityCancel"
    />
</template>


<script lang="ts">
import axios, { AxiosResponse } from 'axios';
import { ElMessage, ElMessageBox, ElPagination, UploadFile, ElTable, ElTableColumn } from 'element-plus';
import { defineComponent, onMounted, ref, nextTick, watch } from 'vue';
import AutoCompleteInput from '@/components/common/AutoCompleteInput.vue';
import MaterialSelect from '@/components/common/MaterialSelect.vue';
import UserSelect from '@/components/common/UserSelect.vue';
import OrganizationSelect from '@/components/common/OrganizationSelect.vue';
import DepartmentSelect from '@/components/common/DepartmentSelect.vue';
import CategorySelect from '../common/CategorySelect.vue';
import type { FormInstance, FormRules } from 'element-plus'
import { UploadFilled, Plus, ZoomIn, Delete, ArrowUp, ArrowDown, ArrowRight, Search, Setting, Close, Loading } from '@element-plus/icons-vue'
import { ErrorCode, ImportResponseData, ReminderSettingResponseData, ResponseData, ResponseStatus, UploadImageResponseData } from '@/components/common/response';
import { getErrorMessage, PageType } from '@/components/common/errorMessage';
import { Operation, SearchRequest } from '@/components/common/request';
import router from '@/router';
import moment from 'moment';
import { resolveExpirationDateFromDay } from '@/components/common/StringUtils';
import { ALL, CSV_MAX_SIZE, IMG_MAX_SIZE, ROLES, ROUTES, screenWidth } from '@/config/constants';
import BarcodeScanner from '@/components/common/BarcodeScanner.vue';
import QuantityEditDrawer from '../common/QuantityEditDrawer.vue';
import ImgLoading from '@/components/common/ImgLoading.vue';
import CarouselLoading from '@/components/common/CarouselLoading.vue';

interface InventoryInfo {
    isExpand?: boolean;
    quantity_is_click?: boolean;
    add_update?: Operation;
    inventory_id: string;
    sk: string;
    material_code: string;
    organization_id: string;
    department_id: string;
    category_id: string;
    quantity: number;
    storage_date?: string;
    expiration_date: string;
    location?: string;
    comment?: string;
    images?: string;
    jancode?: string;
    responsible_person?: string,
    organization_name?: string,
    department_name?: string,
    material_name?: string,
    category_name?: string,
    fileList?: UploadFile[],
    imageUrl?: string,
    details?: InventoryInfo[],
    check_result1?: number,
    check_result2?: number,
    check_result3?: number,
    check_result4?: number,
    check_result5?: number,
    check_result6?: number,
}

interface AlarmInfo {
    inventory_id: string,
    sk: string,
    organization_id: string;
    responsible_person: string,
    alarm_setting: string[],
    quantity?: number,
    is_actived: string,
    add_update: Operation,
}

export default defineComponent({
    name: 'MngInventory',
    components: {
        ElTable,
        ElTableColumn,
        ElPagination,
        MaterialSelect,
        OrganizationSelect,
        DepartmentSelect,
        CategorySelect,
        UserSelect,
        AutoCompleteInput,
        UploadFilled,
        Plus,
        ZoomIn,
        Delete,
        ArrowUp,
        ArrowDown,
        ArrowRight,
        Search,
        Loading,
        Setting,
        Close,
        BarcodeScanner,
        QuantityEditDrawer,
        ImgLoading,
        CarouselLoading,
    },
    setup() {
        const isGrid = ref(false);

        const updateGridMode = (value: boolean) => {
            isGrid.value = value;

            mTableData.value.forEach((item: InventoryInfo) => {
                item.isExpand = false;
            });
        }

        const drawer = ref(false);

        //検索条件
        const searchForm = ref<any>({
            material_code: '',
            organization_id: ALL,
            department_id: ALL,
            category_id: ALL,
            material_name: '',
        });

        const searchDepartmentId = ref<string | null>(null);

        const loading = ref<boolean>(true);
        const mTableData = ref<InventoryInfo[]>([]);
        const mTotalPages = ref<number>(0);
        const mTotalRecords = ref<number>(0);
        const mCurrentPage = ref<number>(1);
        const mPageSize = ref<number>(10);

        //画像
        const imageList = ref([]);
        const dialogImageUrl = ref<string>('')
        const dialogVisible = ref<boolean>(false);
        const disabled = ref<boolean>(false);

        const loginUserRole = localStorage.getItem('role');
        const loginUserRoleIsHead = localStorage.getItem('is_head') === '1';
        const loginUserOrganizationId = localStorage.getItem('organization_id');
        const loginUserOrganizationName = localStorage.getItem('organization_name');
        const loginUserDepartmentId = localStorage.getItem('department_id');
        const loginUserDepartmentName = localStorage.getItem('department_name');

        const barcodeScannerRef = ref<any>(null);

        //条形码扫描器状态
        const barcodeScannerState = ref<boolean>(false);

        //台帳
        const form = ref<InventoryInfo>({
            add_update: Operation.unknow,
            inventory_id: '',
            sk: '',
            material_code: '',
            organization_id: '',
            department_id: '',
            category_id: '',
            location: '',
            comment: '',
            quantity: 0,
            storage_date: '',
            expiration_date: '',
            images: '',
            organization_name: '',
            department_name: '',
            category_name: '',
            material_name: '',
            jancode: '',
        });
        //アラート設定
        const alarmForm = ref<AlarmInfo>({
            inventory_id: '',
            sk: '',
            organization_id: '',
            responsible_person: '',
            alarm_setting: [], // 改为数组以支持多选
            quantity: 0,
            is_actived: '0',
            add_update: Operation.unknow,
        });

        const materialNameInput = ref<any>(null);

        const dialogFormVisible = ref<boolean>(false);
        const dialogFormShowDel = ref<boolean>(false);
        const dialogAlarmVisible = ref<boolean>(false);

        const formRef = ref<FormInstance>();
        const alarmFormRef = ref<FormInstance>();

        const rules = ref<FormRules>({
            organization_id: [
                { required: true, message: '組織を選択してください', trigger: 'change' }
            ],
            department_id: [
                { required: true, message: '部門を選択してください', trigger: 'change' }
            ],
            category_id: [
                { required: true, message: 'カテゴリを選択してください', trigger: 'change' }
            ],
            material_code: [
                { required: true, message: 'アイテム名を選択してください', trigger: 'change' }
            ],
            quantity: [
                { required: true, message: '数量を入力してください', trigger: 'blur' },
                { type: 'number', min: 0, message: '数量は0以上の数字を入力してください', trigger: 'blur' }
            ],
            storage_date: [
                { required: true, message: '保管日を選択してください', trigger: 'change' }
            ],
            expiration_date: [
                { required: true, message: '有効期限を選択してください', trigger: 'change' }
            ]
        });


        // アラート設定
        // TODO 没有起效果，原因不明
       const alarmRules = ref<FormRules>({
            responsible_person: [
                { required: true, message: '責任者を選択してください', trigger: 'change' }
            ],
            alarm_setting: [
                { required: true, type: 'array', min: 1, message: 'アラート設定を選択してください', trigger: 'change' }
            ],
        });

        // 画像预览
        const imageUrl = ref<string>('')

        const fileList = ref<UploadFile[]>([])

        // Add a ref to track the error message
        const jancodeError = ref('');

        const quantityDrawerVisible = ref(false);
        const editingItem = ref<InventoryInfo | null>(null);

        onMounted(() => {
            //  如果没有信息，重新登陆
            if (!loginUserRole) {
                router.push(`/${ROUTES.LOGIN}`);
            }
            getInventoryList();

            updateGridMode(window.innerWidth < screenWidth); // 初始化时检查宽度
        });
        const handleSearchButton = () => {
            drawer.value = false;

            if (materialNameInput.value) {
                materialNameInput.value.submitValue();
            }
            getInventoryList();
        };


        const handleAddButton = () => {
            fileList.value = [];
            form.value = {
                add_update: Operation.add,
                inventory_id: '',
                sk: '',
                material_code: '',
                organization_id: loginUserRole === ROLES.SYSTEM ? '' : (loginUserOrganizationId ?? ''),
                department_id: loginUserRole === ROLES.SYSTEM || (loginUserRole === ROLES.ADMIN && loginUserRoleIsHead) ? '' : (loginUserDepartmentId ?? ''),
                category_id: '',
                quantity: 0,
                storage_date: '',
                expiration_date: '',
                comment: '',
                location: '',
                images: '',
                organization_name: loginUserRole === ROLES.SYSTEM ? '' : (loginUserOrganizationName ?? ''),
                department_name: loginUserRole === ROLES.SYSTEM || (loginUserRole === ROLES.ADMIN && loginUserRoleIsHead) ? '' : (loginUserDepartmentName ?? ''),
                category_name: '',
                material_name: '',
            };
            dialogFormVisible.value = true;
            nextTick(() => {
                formRef.value?.clearValidate();
            });
        };

        const handleExpandButton = (row: InventoryInfo) => {
            row.isExpand = true;
        };

        const handleCollapseButton = (row: InventoryInfo) => {
            row.isExpand = false;
        };


        // 使用对象存每个 input 的 ref
        const inputRefs = ref<Record<string, any>>({})

        function setInputRef(inventory_id: string, el: any) {
            if (el) inputRefs.value[inventory_id] = el
        }

        const currentEditingItem = ref<InventoryInfo | null>(null);
        const originalQuantity = ref<number>(0);

        const preventBlurOnControls = () => {
            nextTick(() => {
                setTimeout(() => {
                    document.querySelectorAll('.quantity-editor .el-input-number__increase, .quantity-editor .el-input-number__decrease')
                        .forEach(button => {
                            button.addEventListener('mousedown', (e) => {
                                e.preventDefault();
                            });
                        });
                }, 100);
            });
        };

        const handleClickQuantityButtonWebMode = (item: InventoryInfo) => {
                        item.quantity_is_click = true;
            nextTick(() => {
                const inputRef = inputRefs.value[item.inventory_id];
                if (inputRef) {
                    inputRef.focus();
                    preventBlurOnControls();
                }
            });
        };

        const handleQuantityFocus = (item: InventoryInfo) => {
            currentEditingItem.value = item;
            originalQuantity.value = item.quantity;
            preventBlurOnControls();
        };

        const handleQuantityBlur = async (row: InventoryInfo) => {
            if (originalQuantity.value !== row.quantity) {
                try {
                    loading.value = true;

                    const res: AxiosResponse<ResponseData<InventoryInfo>> = await axios.put(`/api/inventories/${row.inventory_id}`, {
                        sk: row.sk,
                        quantity: row.quantity,
                    });

                    if (res.status === ResponseStatus.SUCCESS && res.data.code === ErrorCode.OK) {
                        await getInventoryList();
                    } else {
                        ElMessage.error(getErrorMessage(PageType.INVENTORY, res.data.code));
                    }
                } catch (error) {
                    row.quantity = originalQuantity.value;
                    ElMessage.error(getErrorMessage(PageType.INVENTORY));
                } finally {
                    loading.value = false;
                    currentEditingItem.value = null;
                }
            }

            row.quantity_is_click = false;
            currentEditingItem.value = null;
        };

        const handleClickQuantityButton = (item: InventoryInfo) => {
            editingItem.value = item;
            quantityDrawerVisible.value = true;
        };

        const handleQuantityConfirm = async (newQuantity: number) => {
            if (!editingItem.value) return;
            
            try {
                loading.value = true;
                
                const res: AxiosResponse<ResponseData<InventoryInfo>> = await axios.put(`/api/inventories/${editingItem.value.inventory_id}`, {
                    sk: editingItem.value.sk,
                    quantity: newQuantity,
                });
                
                if (res.status === ResponseStatus.SUCCESS && res.data.code === ErrorCode.OK) {
                    await getInventoryList();
                    ElMessage.success('数量が更新されました');
                } else {
                    ElMessage.error(getErrorMessage(PageType.INVENTORY, res.data.code));
                }
            } catch (error) {
                ElMessage.error(getErrorMessage(PageType.INVENTORY));
            } finally {
                loading.value = false;
                editingItem.value = null;
            }
        };

        const handleQuantityCancel = () => {
            editingItem.value = null;
        };

        const handleEditButton = (row: InventoryInfo, dialogShowDel?: boolean) => {
            dialogFormVisible.value = true;
            dialogFormShowDel.value = dialogShowDel || false;
            nextTick(async () => {
                formRef.value?.resetFields();
                fileList.value = []
                form.value = {
                    add_update: Operation.update,
                    inventory_id: row.inventory_id,
                    sk: row.sk,
                    material_code: row.material_code,
                    organization_id: row.organization_id,
                    department_id: row.department_id,
                    category_id: row.category_id,
                    location: row.location,
                    quantity: row.quantity,
                    storage_date: row.storage_date,
                    expiration_date: row.expiration_date,
                    comment: row.comment,
                    images: row.images,
                    organization_name: row.organization_name,
                    department_name: row.department_name,
                    category_name: row.category_name,
                    material_name: row.material_name,
                };
                if (row.images) {
                    let bufImages = row.images.split(',');
                    fileList.value = await getImages(bufImages);
                } else {
                    fileList.value = []
                }
            });

        };

        const handleDeleteButton2 = (inventory_id: string, sk:string) => {
            const row = {
                inventory_id,
                sk,
            };
            handleDeleteButton(row as InventoryInfo);
        };

        const handleDeleteButton = (row: InventoryInfo) => {
            ElMessageBox.confirm('削除しますか？', '削除', {
                confirmButtonText: '削除',
                cancelButtonText: 'キャンセル',
                type: 'warning',
            }).then(() => {
                deleteInventory(row);
            }).catch(() => {
                // console.log('削除キャンセル');
            });
        };

        const handleSizeChange = (size: number) => {
            mPageSize.value = size;
            getInventoryList();
        };
        const handleCurrentChange = (page: number) => {
            mCurrentPage.value = page;
            getInventoryList();
        };

        const handleAddUpdateSubmit = async (formEl: FormInstance | undefined) => {
            if (!formEl) return
            await formEl.validate((valid, fields) => {
                if (valid) {
                    if (form.value.add_update === Operation.add) {
                        addInventory();
                    } else if (form.value.add_update === Operation.update) {
                        updateInventory(form.value);
                    }
                    dialogFormVisible.value = false;
                }
            })
        };


        const handleAlarmButton = (row: InventoryInfo) => {

            alarmForm.value = {
                inventory_id: row.inventory_id,
                sk: row.sk,
                organization_id: row.organization_id,
                responsible_person: row.responsible_person ?? '',
                alarm_setting: [], // 初始化为空数组
                quantity: 0,
                is_actived: '0',
                add_update: Operation.unknow,
            };

            getAlarmSetting(alarmForm.value).then(() => {
                dialogAlarmVisible.value = true;
                if (alarmForm.value.alarm_setting && alarmForm.value.alarm_setting.length > 0) {
                    alarmForm.value.add_update = Operation.update;
                } else {
                    alarmForm.value.add_update = Operation.add;
                }
            });


        };

        // アラート設定
        const handleAlarmSubmit = async (formEl: FormInstance | undefined) => {
            if (!formEl) {
                return;
            }

            // 手动检查必填字段
            if (!alarmForm.value.responsible_person || alarmForm.value.responsible_person.trim() === '') {
                ElMessage.error('責任者を選択してください');
                return;
            }

            if (!alarmForm.value.alarm_setting || !Array.isArray(alarmForm.value.alarm_setting) || alarmForm.value.alarm_setting.length === 0) {
                ElMessage.error('アラート設定を選択してください');
                return;
            }

            if (alarmForm.value.alarm_setting.includes('在庫数') && !alarmForm.value.quantity) {
                ElMessage.error('在庫数を入力してください');
                return;
            }
            

            try {
                const valid = await formEl.validate();

                if (valid) {
                    saveAlarmSetting(alarmForm.value);
                    dialogAlarmVisible.value = false;
      
                }
            } catch (error) {
                ElMessage.error('必須項目を入力してください');
            }
        };

        // 画像変更
        const handleImageChange = (file: UploadFile) => {
            if (fileList.value.length >= 4) {
                ElMessage.warning('最大3枚までアップロードできます。')
                return false
            }
            
            fileList.value.push(file)
            imageUrl.value = URL.createObjectURL(file.raw || new Blob())
        }

        // 画像削除
        const handleRemove = (file: UploadFile) => {
            // 触发画面重绘
            const index = fileList.value.findIndex(item => item.name === file.name);
            if (index !== -1) {
                fileList.value.splice(index, 1);
                // 触发视图更新
                fileList.value = [...fileList.value];
            }
        }

        // 画像预览
        const handlePictureCardPreview = (file: UploadFile) => {
            dialogImageUrl.value = file.url || ''
            dialogVisible.value = true
        }

        const handleExceed = (files: File[]) => {
            //ElMessage.warning(`最多只能上传4张图片。当前选择了 ${files.length} 张图片，已有 ${fileList.value.length} 张图片。`)
            ElMessage.warning('最大3枚までアップロードできます。')
        }


        /******* API ******************* */

        const getInventoryList = async () => {
            try {

                loading.value = true;

                const params: SearchRequest = {
                    page: mCurrentPage.value,
                    limit: mPageSize.value,
                    material_code: searchForm.value.material_code,
                    organization_id: searchForm.value.organization_id === ALL ? '' : searchForm.value.organization_id,
                    department_id: searchForm.value.department_id === ALL ? '' : searchForm.value.department_id,
                    category_id: searchForm.value.category_id === ALL ? '' : searchForm.value.category_id,
                    material_name: searchForm.value.material_name,
                };

                const res: AxiosResponse<ResponseData<InventoryInfo[]>> = await axios.get('/api/inventories/search', { params });
                if (res.status === ResponseStatus.SUCCESS && res.data.code === ErrorCode.OK) {
                    searchDepartmentId.value = searchForm.value.department_id === ALL ? null : searchForm.value.department_id;

                    const { total, totalPages, currentPage, data } = res.data;

                    mCurrentPage.value = currentPage || 1;
                    mTotalPages.value = totalPages || 0;
                    mTotalRecords.value = total || 0;
                    mTableData.value = data || [];
                } else {
                    ElMessage.error(getErrorMessage(PageType.INVENTORY, res.data.code));
                }
            } catch (error) {
                ElMessage.error(getErrorMessage(PageType.INVENTORY));
            } finally {
                loading.value = false;
            }
        };


        const addInventory = async () => {
            try {
                loading.value = true;

                //画像アップロード
                let images = [];
                if (fileList.value.length > 0) {
                    const imgaeInfos = await uploadImages(fileList.value);
                    if (imgaeInfos) {
                        for (const imgaeInfo of imgaeInfos) {
                            images.push(imgaeInfo.id);
                        }
                    } else {
                        ElMessage.error('画像アップロード失敗しました。');
                        return;
                    }
                }

                form.value.material_name = form.value.material_name || '';
                form.value.organization_name = form.value.organization_name || '';
                form.value.department_name = form.value.department_name || '';
                form.value.category_name = form.value.category_name || '';
                form.value.images = images.join(',');

                const res: AxiosResponse<ResponseData<InventoryInfo>> = await axios.post('/api/inventories', form.value);
                if (res.status === ResponseStatus.SUCCESS && res.data.code === ErrorCode.OK) {
                    await getInventoryList();
                } else {
                    ElMessage.error(getErrorMessage(PageType.INVENTORY, res.data.code));
                }
            }
            catch (error) {
                ElMessage.error(getErrorMessage(PageType.INVENTORY));
            } finally {
                loading.value = false;
            }
        };


        const updateInventory = async (row: InventoryInfo) => {
            try {
                loading.value = true;

                //画像アップロード
                let images = [];
                const rowImages = row.images?.split(',') || [];

                fileList.value.map(file => file.name).forEach(fileName => {
                    if(rowImages.includes(fileName)) {
                        images.push(fileName);
                    }
                });

                // 新的图片列表中不包含rowImages中的图片，这些是需要上传的
                const newfileList = fileList.value.filter(file => !rowImages.includes(file.name));

                if (newfileList.length > 0) {
                    const imgaeInfos = await uploadImages(newfileList);
                    if (imgaeInfos) {
                        for (const imgaeInfo of imgaeInfos) {
                            images.push(imgaeInfo.id);
                        }
                    } else {
                        ElMessage.error('画像アップロード失敗しました。');
                        return;
                    }
                }
                
                form.value.material_name = form.value.material_name || '';
                form.value.organization_name = form.value.organization_name || '';
                form.value.department_name = form.value.department_name || '';
                form.value.category_name = form.value.category_name || '';
                form.value.images = images.join(',');

                const res: AxiosResponse<ResponseData<InventoryInfo>> = await axios.put(`/api/inventories/${row.inventory_id}`, form.value);
                if (res.status === ResponseStatus.SUCCESS && res.data.code === ErrorCode.OK) {
                    await getInventoryList();
                } else {
                    ElMessage.error(getErrorMessage(PageType.INVENTORY, res.data.code));
                }
            }
            catch (error) {
                ElMessage.error(getErrorMessage(PageType.INVENTORY));
            } finally {
                loading.value = false;
            }
        };


        const deleteInventory = async (row: InventoryInfo) => {
            dialogFormVisible.value = false;
            try {
                loading.value = true;
                const res: AxiosResponse<ResponseData> = await axios.delete(`/api/inventories/${row.inventory_id}`, {
                    data: {
                        sk: row.sk
                    }
                });
                if (res.status === ResponseStatus.SUCCESS && res.data.code === ErrorCode.OK) {
                    await getInventoryList();
                } else {
                    ElMessage.error(getErrorMessage(PageType.INVENTORY, res.data.code));
                }
            }
            catch (error) {
                ElMessage.error(getErrorMessage(PageType.INVENTORY));
            } finally {
                loading.value = false;
            }
        };


        const getAlarmSetting = async (row: AlarmInfo) => {
            try {
                loading.value = true;
                const res: AxiosResponse<ReminderSettingResponseData> = await axios.get(`/api/reminder-settings/${row.inventory_id}`);
                if (res.status === ResponseStatus.SUCCESS && res.data.code === ErrorCode.OK) {
                    alarmForm.value = {
                        ...alarmForm.value,
                        inventory_id: res.data.inventory_id || '',
                        responsible_person: res.data.user_id?.toString() || '',
                        alarm_setting: res.data.type?.split(',') || [], // 将字符串分割为数组
                        quantity: res.data.quantity || 0,
                        is_actived: res.data.is_actived?.toString() || '0',
                    };
                } else {
                    alarmForm.value = {
                        ...alarmForm.value,
                        is_actived: '0',
                        inventory_id: row.inventory_id,
                        alarm_setting: [], // 初始化为空数组
                    };
                }
            } catch (error) {
                ElMessage.error(getErrorMessage(PageType.INVENTORY));
            } finally {
                loading.value = false;
            }
        };


        const saveAlarmSetting = async (row: AlarmInfo) => {
            try {
                loading.value = true;
                const dataToSend = {
                    ...alarmForm.value,
                    alarm_setting: alarmForm.value.alarm_setting.join(','), // 将数组转换为逗号分隔的字符串
                };

                const res: AxiosResponse<ResponseData<AlarmInfo[]>> = await axios.post(`/api/reminder-settings`, dataToSend);
                if (res.status === ResponseStatus.SUCCESS && res.data.code === ErrorCode.OK) {
                    await getInventoryList();
                } else {
                    ElMessage.error(getErrorMessage(PageType.INVENTORY, res.data.code));
                }
            } catch (error) {
                ElMessage.error(getErrorMessage(PageType.INVENTORY));
            } finally {
                loading.value = false;
            }
        };


        const getImages = async (image_ids: string[]) => {
            var fileLst: UploadFile[] = [];

            for (const image_id of image_ids) {
                const res = await axios.get(`/api/images/download/${image_id}`, { responseType: 'blob' });
                if (res.status === ResponseStatus.SUCCESS && res.data.code === undefined) {
                    const uid = parseInt(image_id);
                    fileLst.push({
                        url: URL.createObjectURL(res.data),
                        uid: uid,
                        name: image_id,
                        raw: res.data,
                        status: 'ready'
                    });
                } else {
                    ElMessage.error('画像情報の取得に失敗しました。');
                }
            }

            return fileLst;
        };

        const getImages2 = async (image_ids: string[]) => {
            var imgUrl = '';
            for (const image_id of image_ids) {
                const res = await axios.get(`/api/images/download/${image_id}`, { responseType: 'blob' });
                if (res.status === ResponseStatus.SUCCESS && res.data.code === undefined) {
                    // 预览(单张 本画面特定)
                    imgUrl = URL.createObjectURL(res.data);
                } else {
                    ElMessage.error('画像情報の取得に失敗しました。');
                }
            }

            return imgUrl;
        };


        const uploadImages = async (fileList: UploadFile[]) => {
            if (fileList.length > 0) {
                const formData = new FormData()
                
                // 统计所有文件的总大小
                let totalSize = 0;
                for (const tFile of fileList) {
                    if (tFile.raw) {
                        totalSize += tFile.raw.size;
                    }
                }
                
                // 检查总大小是否超过限制
                if (totalSize > IMG_MAX_SIZE) {
                    ElMessage.error('ファイルが大きすぎます。');
                    return '';
                }
                
                for (const tFile of fileList) {
                    formData.append('files', tFile.raw || new Blob())
                    //文件过大 100K
                    if (tFile.raw && tFile.raw.size > IMG_MAX_SIZE) {
                        ElMessage.error('ファイルが大きすぎます。');
                        return '';
                    }
                }

                try {
                    const res: AxiosResponse<UploadImageResponseData> = await axios.post('/api/images/upload', formData, {
                        headers: {
                            'Content-Type': 'multipart/form-data'
                        }
                    });

                    if (res.status === ResponseStatus.SUCCESS && res.data.code === ErrorCode.OK) {
                        ElMessage.success('画像アップロード成功');
                        return res.data.images; // 返回值
                    } else {
                        ElMessage.error('画像アップロード失敗');
                        return '';
                    }
                } catch (error) {
                    ElMessage.error('画像アップロード失敗')
                    return ''; // 返回值
                }
            } else {
                ElMessage.error('ファイルが無効です')
                return ''; // 返回值
            }
        }

        const handleExportButton = () => {
            ElMessageBox.confirm('在庫一覧をエクスポートしますか？', 'CSVエクスポート', {
                confirmButtonText: 'エクスポート',
                cancelButtonText: 'キャンセル',
                type: 'warning',
            }).then(() => {
                exportMaterial();
            }).catch(() => {
                // console.log('削除キャンセル');
            });
        };

        const exportMaterial = async () => {
            try {
                const res: AxiosResponse<any> = await axios.post(`/api/inventories/export`, {
                    responseType: 'blob'  // 设置响应类型为 blob
                });
                if (res.status === ResponseStatus.SUCCESS && res.data.code === undefined) {
                    // 创建 Blob 对象并触发文件下载
                    const bom = '\uFEFF'; // UTF-8 BOM
                    const blob = new Blob([bom + res.data], { type: 'text/csv;charset=utf-8;' });
                    const link = document.createElement('a');
                    link.href = URL.createObjectURL(blob);
                    link.download = '在庫一覧_' + moment(new Date().toISOString()).format('YYYYMMDDHHmmss') + '.csv';  // 指定下载的文件名
                    link.click();
                } else {
                    ElMessage.error('CSVエクスポート失敗しました。');
                }
            }
            catch (error) {
                ElMessage.error('CSVエクスポート失敗しました。');
            }
        };

        const handleImportButton = (file: UploadFile) => {
            if (file.raw) {
                const formData = new FormData()
                formData.append('file', file.raw)

                //文件过大 100K
                if (file.raw.size > CSV_MAX_SIZE) {
                    ElMessage.error('ファイルが大きすぎます。');
                    return;
                }

                loading.value = true;
                axios.post('/api/inventories/import', formData, {
                    headers: {
                        'Content-Type': 'multipart/form-data'
                    }
                }).then((res: AxiosResponse<ImportResponseData>) => {
                    if (res.status === ResponseStatus.SUCCESS && res.data.code === ErrorCode.OK) {
                        getInventoryList();
                        ElMessage.success('CSVインポート成功');
                    } else {
                        ElMessage.error('CSVインポート失敗しました。');
                        loading.value = false;
                    }

                }).catch(error => {
                    ElMessage.error('CSVインポート失敗しました。');
                    loading.value = false;
                });
            } else {
                ElMessage.error('ファイルが無効です')
            }
        }

        const updateOrganizationId = (newOrganizationId: string) => {
            form.value.organization_id = newOrganizationId;
        }

        const updateCategoryId = (newCategoryId: string) => {
            form.value.category_id = newCategoryId;
        }

        const updateExpirationDate = (newExpirationDay: number) => {
            if (form.value.add_update === Operation.update) {
                return;
            }
            const calculatedDate = resolveExpirationDateFromDay(
                newExpirationDay,
                form.value.storage_date
            );
            if (!calculatedDate) {
                return;
            }
            form.value.expiration_date = calculatedDate;
        }

        const handleScanStart = () => {
            // For barcode scan, only validate organization_id
            if (!form.value.organization_id) {
                errors.value.jancode = '組織を選択してください';
                return;
            }
            
            // Clear any error
            errors.value.jancode = '';
            
            barcodeScannerState.value = true;
            
            nextTick(() => {
                if (barcodeScannerRef.value) {
                    (barcodeScannerRef.value as any).startScanning();
                }
            });
        };

        const handleScanSuccess = async (jancode: string) => {
            try {
                getJanCodeInfo(jancode).then(materialInfoResult => {
                // Check if result is an array and use first item
                const materialInfo = Array.isArray(materialInfoResult) && materialInfoResult.length > 0 
                    ? materialInfoResult[0] 
                    : materialInfoResult;
                    
                if (materialInfo && Object.keys(materialInfo).length > 0) {
                    // Prepare all data before updating the form to avoid flicker
                    form.value.material_code = materialInfo.material_code;
                    form.value.material_name = materialInfo.name;
                    form.value.category_id = materialInfo.category_id;
                    form.value.jancode = materialInfo.jancode;
                    const calculatedDate = resolveExpirationDateFromDay(
                        materialInfo.expiration_day,
                        form.value.storage_date
                    );
                    if (calculatedDate) {
                        form.value.expiration_date = calculatedDate;
                    }
                } else {
                    errors.value.jancode = 'アイテムマスタにありません。アイテムマスタに登録してください。';
                }
            });
        }
            catch (error) {
                ElMessage.error('JANコードの取得に失敗しました。');
            }
        }

        const handleJancodeSearch = async (jancode: string) => {
            // For JANcode search, validate both jancode and organization_id
            
            // Check if jancode is empty
            if (!form.value.jancode || form.value.jancode === '') {
                errors.value.jancode = 'JANコードを入力してください';
                return;
            }
            
            // Check if organization is selected
            if (!form.value.organization_id) {
                errors.value.jancode = '組織を選択してください';
                return;
            }
            
            // Clear any error
            errors.value.jancode = '';
            
            getJanCodeInfo(jancode).then(materialInfoResult => {
                const materialInfo = Array.isArray(materialInfoResult) && materialInfoResult.length > 0 
                    ? materialInfoResult[0] 
                    : materialInfoResult;
                    
                if (materialInfo && Object.keys(materialInfo).length > 0) {
                    // Prepare all data before updating the form to avoid flicker
                    form.value.material_code = materialInfo.material_code;
                    form.value.material_name = materialInfo.name;
                    form.value.category_id = materialInfo.category_id;
                    const calculatedDate = resolveExpirationDateFromDay(
                        materialInfo.expiration_day,
                        form.value.storage_date
                    );
                    if (calculatedDate) {
                        form.value.expiration_date = calculatedDate;
                    }
                } else {
                    errors.value.jancode = 'アイテムマスタにありません。アイテムマスタに登録してください。';
                }
            });
        };

        const getJanCodeInfo = async (jancode: string) => {
            try {
                const params = {
                    jancode: jancode,
                    organization_id: form.value.organization_id,
                }

                const res: AxiosResponse<ResponseData<any>> = await axios.get(`/api/materials/jancode`, { params });
                if (res.status === ResponseStatus.SUCCESS && res.data.code === ErrorCode.OK) {
                    return res.data.data;
                }
                return null;
            } catch (error) {
                return null;
            }
        }

        // Add a watcher for the dialog visibility
        watch(() => dialogFormVisible.value, (newVal, oldVal) => {
            // If the dialog is being closed (changing from true to false)
            if (oldVal === true && newVal === false) {
                // Reset the barcode scanner state
                barcodeScannerState.value = false;
                
                // Also stop scanning if it's in progress
                if (barcodeScannerRef.value) {
                    (barcodeScannerRef.value as any).resetScannerState();
                }
            }
        });

        // Add a reactive object to store errors
        const errors = ref({
            jancode: ''
        });

        // Add watchers to clear errors when the corresponding fields change
        watch(() => form.value.jancode, (newVal) => {
            if (newVal && errors.value.jancode === 'JANコードを入力してください') {
                errors.value.jancode = '';
            }
        });

        watch(() => form.value.organization_id, (newVal) => {
            if (newVal && errors.value.jancode === '組織を選択してください') {
                errors.value.jancode = '';
            }
        });

        const getImgUrls = (images: string) => {
            let bufImages: string[] = images.split(',');

            return bufImages;
        };

        const handleDeleteReminderSetting = async () => {
            try {
                await ElMessageBox.confirm(
                    'このアラート設定を削除しますか？',
                    '確認',
                    {
                        confirmButtonText: '削除',
                        cancelButtonText: 'キャンセル',
                        type: 'warning',
                    }
                );

                // Call API to delete reminder setting using the inventory_id and sk
                const response = await axios.delete(`/api/reminder-settings/${alarmForm.value.inventory_id}`, {
                    data: { sk: alarmForm.value.sk }
                });
                
                if (response.status === 200) {
                    ElMessage.success('アラート設定を削除しました');
                    dialogAlarmVisible.value = false;
                    // Refresh the inventory list or update the UI as needed
                    await getInventoryList();
                }
            } catch (error) {
                if (error !== 'cancel') {
                    console.error('Failed to delete reminder setting:', error);
                    ElMessage.error('アラート設定の削除に失敗しました');
                }
            }
        };

        return {
            loginUserRole,
            loginUserRoleIsHead,
            isGrid,
            searchForm,
            loading,
            mTableData,
            mTotalPages,
            mTotalRecords,
            mCurrentPage,
            mPageSize,
            handleAddButton,
            handleSearchButton,
            handleEditButton,
            handleDeleteButton,
            handleDeleteButton2,
            handleAlarmButton,
            handleSizeChange,
            handleCurrentChange,
            updateGridMode,
            updateExpirationDate,
            updateOrganizationId,
            updateCategoryId,
            dialogFormVisible,
            form,
            formRef,
            rules,
            handleAddUpdateSubmit,
            dialogAlarmVisible,
            dialogFormShowDel,
            alarmForm,
            alarmFormRef,
            alarmRules,
            imageUrl,
            handleAlarmSubmit,
            handleImageChange,
            imageList,
            dialogImageUrl,
            dialogVisible,
            disabled,
            handleRemove,
            handlePictureCardPreview,
            fileList,
            handleExceed,
            handleExportButton,
            handleImportButton,
            handleExpandButton,
            handleCollapseButton,
            handleClickQuantityButton,
            handleQuantityConfirm,
            handleQuantityCancel,
            handleClickQuantityButtonWebMode,
            handleQuantityFocus,
            handleQuantityBlur,
            quantityDrawerVisible,
            editingItem,
            materialNameInput,
            ROLES,
            searchDepartmentId,
            barcodeScannerState,
            barcodeScannerRef,
            handleScanStart,
            handleJancodeSearch,
            jancodeError,
            errors,
            handleScanSuccess,
            drawer,
            handleDeleteReminderSetting,
            setInputRef,
            getImgUrls,
            moment
        }
    }
});
</script>


<style scoped>
    .thumbnail {
        width: 30px;
        height: 30px;
        object-fit: cover;
    }

    .carousel-image {
        width: 100%;
        height: 100%;
        object-fit: contain;
    }

    .barcode-scanner-container {
        width: 100%;
        height: 100%;
        object-fit: contain;
    }

    :deep(.el-input-group__append) {
    padding: 0;
    margin: 0;
    }

    :deep(.el-input-group__append .el-button) {
        margin: 0;
        border-radius: 0 4px 4px 0;
    }

    .big-font-input :deep(.el-input__inner) {
        font-size: 32px;
    }
</style>